import { useRef, useState, type ReactNode } from "react";
import { useSwipeDefer } from "@/lib/theme";

const THRESHOLD = 72;   // past this, the action fires
const CLAMP = 116;      // the row never travels further than this
const AXIS_LOCK = 6;    // px of movement before we decide the gesture's axis

/** What a swipe in one direction does, and how it announces itself. */
export interface SwipeAction {
  label: string;
  /** Background and text colour of the strip the row uncovers. */
  bg: string;
  ink: string;
  onFire: () => void;
}

/**
 * §15.1 — iOS swipe actions, for any row.
 *
 * `right` is what a swipe to the right does (its strip shows on the left);
 * `left` is a swipe to the left. Past 72px the action fires on release; below
 * that the row springs back. A direction with no action doesn't travel.
 *
 * The axis is locked on first movement: until the finger has travelled 6px we
 * don't know whether this is a swipe or a scroll, and after that we commit. A
 * horizontal gesture takes over the row; a vertical one is handed back to the
 * page and ignored for the rest of the touch, so the list still scrolls.
 */
export function SwipeActions({ children, left, right, disabled, radius = 12 }: {
  children: ReactNode;
  left?: SwipeAction;
  right?: SwipeAction;
  disabled?: boolean;
  radius?: number;
}) {
  const [dx, setDx] = useState(0);
  // Mirrored in a ref so releasing reads the distance the finger actually
  // travelled, not whatever the last committed render closed over.
  const dxRef = useRef(0);
  const [dragging, setDragging] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const axis = useRef<"undecided" | "x" | "y">("undecided");
  // A swipe ends with a pointerup over the row, which the browser follows with
  // a click. Without this, swiping a row also opened whatever tapping it opens.
  const swallowClick = useRef(false);

  function down(e: React.PointerEvent) {
    if (disabled || e.pointerType === "mouse") return;
    start.current = { x: e.clientX, y: e.clientY };
    axis.current = "undecided";
  }

  function move(e: React.PointerEvent) {
    const s = start.current;
    if (!s) return;
    const ddx = e.clientX - s.x;
    const ddy = e.clientY - s.y;

    if (axis.current === "undecided") {
      if (Math.abs(ddx) < AXIS_LOCK && Math.abs(ddy) < AXIS_LOCK) return;
      axis.current = Math.abs(ddx) > Math.abs(ddy) ? "x" : "y";
      if (axis.current === "x") setDragging(true);
    }
    if (axis.current !== "x") return;

    const next = Math.max(left ? -CLAMP : 0, Math.min(right ? CLAMP : 0, ddx));
    dxRef.current = next;
    setDx(next);
  }

  function up() {
    const travelled = dxRef.current;
    if (axis.current === "x") swallowClick.current = true;
    start.current = null;
    axis.current = "undecided";
    setDragging(false);
    dxRef.current = 0;
    setDx(0);
    if (travelled <= -THRESHOLD) left?.onFire();
    else if (travelled >= THRESHOLD) right?.onFire();
  }

  // Labels fade in proportionally, so the row tells you what it will do before
  // you commit to it.
  const progress = Math.min(1, Math.abs(dx) / THRESHOLD);
  const shown = dx > 0 ? right : dx < 0 ? left : undefined;

  return (
    <div style={{ position: "relative", overflow: "hidden", borderRadius: radius, touchAction: "pan-y" }}>
      {shown && (
        <div
          aria-hidden="true"
          style={{
            position: "absolute", inset: 0, display: "flex", alignItems: "center",
            justifyContent: dx > 0 ? "flex-start" : "flex-end",
            padding: "0 16px", borderRadius: radius,
            background: shown.bg, color: shown.ink,
            fontSize: 12.5, fontWeight: 500, opacity: progress,
          }}
        >
          {dx > 0 ? `${shown.label} ›` : `‹ ${shown.label}`}
        </div>
      )}
      <div
        onPointerDown={down}
        onPointerMove={move}
        onPointerUp={up}
        onPointerCancel={up}
        onClickCapture={(e) => {
          if (!swallowClick.current) return;
          swallowClick.current = false;
          e.stopPropagation();
          e.preventDefault();
        }}
        style={{
          position: "relative",
          transform: `translateX(${dx}px)`,
          // No transition while the finger is down — the row has to track it.
          // The spring back on release is the only animated part.
          transition: dragging ? "none" : "transform 0.18s cubic-bezier(.2,.7,.3,1)",
        }}
      >
        {children}
      </div>
    </div>
  );
}

/**
 * The task row's swipes: left past 72px completes (or reopens); right defers
 * the due date by a week, when that is switched on in Settings.
 */
export function SwipeRow({ children, onComplete, onDefer, completeLabel = "Complete", disabled }: {
  children: ReactNode;
  onComplete?: () => void;
  onDefer?: () => void;
  completeLabel?: string;
  disabled?: boolean;
}) {
  const [deferEnabled] = useSwipeDefer();
  const canDefer = deferEnabled === "on" && !!onDefer;
  return (
    <SwipeActions
      disabled={disabled}
      left={onComplete ? { label: completeLabel, bg: "var(--goodTint)", ink: "var(--goodInk)", onFire: onComplete } : undefined}
      right={canDefer ? { label: "Defer 1 week", bg: "var(--warnTint)", ink: "var(--warn)", onFire: onDefer! } : undefined}
    >
      {children}
    </SwipeActions>
  );
}
